import { Box, Text, Link } from '@sparrowengg/twigs-react';
import { tooltipContentValues } from '../constants';
import { RefDivType } from 'commons/types';

export const useTourSteps = ({
  eventHandlerRef,
  codeBlockRef,
  validateRef,
  consoleRef,
  eventSimulationRef,
  iparamsRef,
  oauthRef,
  publishAppRef
}: {
  eventHandlerRef: RefDivType;
  codeBlockRef: RefDivType;
  validateRef: React.RefObject<HTMLButtonElement>;
  consoleRef: RefDivType;
  eventSimulationRef: RefDivType;
  iparamsRef: RefDivType;
  oauthRef: RefDivType;
  publishAppRef: React.RefObject<HTMLButtonElement>;
}) => {
  const createTourSteps = () => {
    return [
      {
        target: eventHandlerRef.current,
        content: [
          <Text size="sm">
            AppNest applications are primarily powered by events.
          </Text>,
          <Text size="sm">
            It requires certain tasks to be performed when a event is triggered.
          </Text>,
          <Text size="sm">
            So, event handlers are the functions that executes when the
            respective event gets triggered.
          </Text>,
          <Text size="sm">
            Refer to the{' '}
            <Link
              css={{ color: '$primary400', cursor: 'pointer' }}
              href={process.env.REACT_APP_SERVERLESS_DOC}
              target="_blank"
            >
              documentation
            </Link>{' '}
            to learn more.
          </Text>,
          <Text size="sm">
            Now, click on the{' '}
            <Box as="span" css={{ color: '$accent400' }}>
              + icon
            </Box>{' '}
            to add a new event.
          </Text>,
          <Text size="sm">
            For example, let’s choose{' '}
            <Box as="span" css={{ color: '$accent400' }}>
              response
            </Box>{' '}
            event group
          </Text>,
          <Text size="sm">
            Then, click on the{' '}
            <Box as="span" css={{ color: '$accent400' }}>
              onSubmissionComplete
            </Box>{' '}
            event to add the event
          </Text>
        ],
        disableBeacon: true,
        placement: 'bottom',
        title: 'Add Event Handlers',
        value: tooltipContentValues.EVENT_HANDLERS
      },
      {
        target: codeBlockRef.current,
        disableBeacon: true,
        content: [
          <Text size="sm">
            This code get executed when the onSubmissionComplete event gets
            triggered
          </Text>,
          <Text size="sm">
            <Box as="span" css={{ color: '$accent400' }}>
              options
            </Box>{' '}
            parameter holds the event payload.
          </Text>,
          <Text size="sm">
            Since your are in development, AppNest provides a way to simulate
            event triggers.
          </Text>,
          <Text size="sm">Click on the Next button to continue.</Text>
        ],
        placement: 'center',
        title: 'Sample Code Snippet',
        value: tooltipContentValues.CODE_SNIPPET
      },
      {
        target: validateRef.current,
        disableBeacon: true,
        offset: -10,
        content: [
          <Text size="sm">
            The{' '}
            <Box as="span" css={{ color: '$accent400' }}>
              simulate
            </Box>{' '}
            button is disabled since the application needs to be validated to
            ensure that there are no compile time or runtime errors.
          </Text>,
          <Text size="sm">
            Click on the{' '}
            <Box as="span" css={{ color: '$accent400' }}>
              validate
            </Box>{' '}
            button and go to the console tab for logs at the bottom
          </Text>
        ],
        placement: 'left',
        title: 'Validate First',
        value: tooltipContentValues.VALIDATE_FIRST
      },
      {
        target: consoleRef.current,
        disableBeacon: true,
        content: [
          <Text size="sm">
            <Box as="span" css={{ color: '$accent400' }}>
              Console tab
            </Box>{' '}
            is responsible for displaying all the logs related to the
            application
          </Text>,
          <Text size="sm">
            You should be able to see the validation successful message in the
            console tab.
          </Text>
        ],
        placement: 'top-start',
        title: 'Application Logs',
        value: tooltipContentValues.APPLICATION_LOGS
      },
      {
        target: eventSimulationRef.current,
        content: [
          <Text size="sm">
            It’s time for simulating the onSubmissionComplete Event
          </Text>,
          <Text size="sm">
            Note that this event payload will be sent to the respective event
            handler in the args parameter.
          </Text>
        ],
        offset: 80,
        placement: 'right-end',
        title: 'Event Simulation',
        value: tooltipContentValues.EVENT_SIMULATION
      },
      {
        target: consoleRef.current,
        content: [
          <Text size="sm">
            The logs for the event simulation should be displayed in the console
            tab.
          </Text>
        ],
        placement: 'top-start',
        title: 'Event Simulation Logs',
        value: tooltipContentValues.EVENT_SIMULATION_LOGS
      },
      {
        target: iparamsRef.current,
        disableBeacon: true,
        content: [
          <Text size="sm">
            The logs for the event simulation should be displayed in the console
            tab.
          </Text>,
          <Text size="sm">
            Check out the{' '}
            <Link
              css={{ color: '$primary400', cursor: 'pointer' }}
              href={process.env.REACT_APP_IPARAM_DOC}
              target="_blank"
            >
              documentation
            </Link>{' '}
            to learn more
          </Text>
        ],
        placement: 'right-start',
        title: 'IParams',
        value: tooltipContentValues.IPARAMS
      },
      {
        target: oauthRef.current,
        disableBeacon: true,
        content: [
          <Text size="sm">
            If you wish to use{' '}
            <Box as="span" css={{ color: '$accent400' }}>
              third-party OAuth
            </Box>{' '}
            within your application, the respective credentials can be specified
            in this page.
          </Text>,
          <Text size="sm">
            The user will be asked to authorize their account to the third-party
            service during the installation.
          </Text>,
            <Text size="sm">
            Check out the{' '}
            <Link
              css={{ color: '$primary400', cursor: 'pointer' }}
              href={process.env.REACT_APP_OAUTH_DOC}
              target="_blank"
            >
              documentation
            </Link>{' '}
            to learn more
          </Text>
        ],
        placement: 'right-start',
        title: 'OAuth',
        value: tooltipContentValues.OAUTH
      },
      {
        target: publishAppRef.current,
        disableBeacon: true,

        content: [
          <Text size="sm">We are at the end of the tour.</Text>,
          <Text size="sm">
            Click on the Publish button to upload your app to the AppNest
            developer portal
          </Text>
        ],
        placement: 'bottom',
        title: 'Publish App',
        value: tooltipContentValues.PUBLISH_APP
      }
    ];
  };

  return {
    createTourSteps
  };
};
